import { css } from 'aphrodite/no-important';
import { ToolTip } from 'components';
import React, { FC, useEffect, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { RootState } from 'redux/store';
import { GUEST_USER_REGEX, ROUTE } from 'util/constants';
import { style } from './style';
import { readFromStorage, writeToStorage } from 'util/storage';

export interface WelcomeSectionProps {
  onLogout: () => void;
}

export const WelcomeSection: FC<WelcomeSectionProps> = ({ onLogout }) => {
  const [hovered, setHovered] = useState(false);
  const [focus, setFocus] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const userData = useSelector((state: RootState) => state.user.data);
  const cmsData = useSelector((state: RootState) => state.widget.cms.text.header);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const isAchievementPopupActive = !loggingOut && ((!isTouchDevice && hovered) || (isTouchDevice && focus && hovered));
  const styles = style({ isUserLoggedIn: !!userData, activePopUp: isAchievementPopupActive });
  const { welcome } = cmsData;

  const HIDE_TOOLTIP_KEY = 'hideToolTip_' + userData?.code;
  const [hideToolTip, setHideToolTip] = useState(() => !!readFromStorage(HIDE_TOOLTIP_KEY));

  const handleDismissTooltip = useCallback(() => {
    setHideToolTip(true);
    writeToStorage(HIDE_TOOLTIP_KEY, true);
  }, [HIDE_TOOLTIP_KEY]);

  useEffect(() => {
    function handleDocumentTouchStartEvent(event: any) {
      if (containerRef.current && !containerRef.current.contains(event.target as Element)) {
        reset();
      }
    }

    if (containerRef.current) document.addEventListener('touchstart', handleDocumentTouchStartEvent);
    return () => document.removeEventListener('touchstart', handleDocumentTouchStartEvent);
  }, [containerRef]);

  const getWelcomeString = () => {
    const maxChar = 15;
    let userName: string = userData?.user_first_name || '';
    const isGuestUser = GUEST_USER_REGEX.test(userName);

    if (userName.length > maxChar) {
      userName = userName.substr(0, maxChar) + '&hellip;';
    }

    if (isGuestUser) {
      return welcome.guest_text;
    } else {
      return welcome.text.replace('{user_name}', `${userName}`);
    }
  };

  const handleLogout = () => {
    setLoggingOut(true);
    reset();
    onLogout();
  };

  const onMouseEnter = () => {
    setHovered(true);
    setFocus(true);
  };

  const onMouseLeave = () => {
    setHovered(false);
    // On touch devices this is like clicking outside so we want to lose the focus
    if (isTouchDevice) setFocus(false);
  };

  const reset = () => {
    setHovered(false);
    setFocus(false);
  };

  const welcomeLink = userData && (
    <div
      key='welcome-link'
      className={css(styles.link, styles.welcomeLinkContainer)}
      ref={(node) => (containerRef.current = node)}
      onMouseLeave={onMouseLeave}>
      <img
        src={'/images/achievement-medal.png'}
        alt='achievement medal'
        title={welcome.achievements_tooltip.hint}
        onMouseEnter={onMouseEnter}
        onTouchStart={() => {
          setIsTouchDevice(true);
          onMouseEnter();
        }}
      />

      {!hideToolTip && (
        <ToolTip top='70px' left='-28px' onClick={handleDismissTooltip} onClickOutside={handleDismissTooltip}>
          {welcome.achievements_tooltip.hint}
        </ToolTip>
      )}

      <div className={css(styles.welcomeLink)}>
        <span
          onTouchStart={() => {
            setIsTouchDevice(true);
            onMouseEnter();
          }}
          className={css(styles.welcomeString)}
          dangerouslySetInnerHTML={{ __html: getWelcomeString() }}
        />
        {/* eslint-disable-next-line */}
        <a onClick={handleLogout}>Log Out</a>
      </div>
      <div className={css(styles.toolTipContainer)}>
        <div className={css(styles.toolTip)}>
          <span className={css(styles.toolTipHeadline)}>{welcome.achievements_tooltip.headline}</span>
          <span
            className={css(styles.toolTipParagraph)}
            dangerouslySetInnerHTML={{ __html: welcome.achievements_tooltip.description }}></span>
          <Link to={ROUTE.ACHIEVEMENTS} onClick={reset}>
            {welcome.achievements_tooltip.link_text}
          </Link>
        </div>
      </div>
    </div>
  );

  return welcomeLink;
};
